import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { PageLoader, useFetch } from "@/lib/hooks";
import { Header } from "@/components/site/Header";
import { Footer, StickyBar } from "@/components/site/StickyBar";
import { SampleBadge, StatusBadge } from "@/components/site/Badges";
import { Lightbox } from "@/components/site/Lightbox";
import { VideoPlayer } from "@/components/site/VideoPlayer";

const MediaTile = ({ m, onOpen }) => (
  <figure className="min-w-0" data-testid={`project-media-${m.id}`}>
    {m.kind === "video" ? (
      <VideoPlayer media={m} testId={`project-video-wrap-${m.id}`} />
    ) : (
      <button onClick={onOpen} className="group block w-full overflow-hidden rounded-md bg-[#0d0d0f]" data-testid={`project-photo-${m.id}`} aria-label={m.caption || "Open photo"}>
        <img src={mediaUrl(m.thumb_url)} alt={m.caption || "Project photo"} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
      </button>
    )}
    {m.caption && <figcaption className="mt-2 text-sm text-zinc-400">{m.caption}</figcaption>}
  </figure>
);

export default function ProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { data, error } = useFetch(() => api.get(`/public/projects/${projectId}`), [projectId]);
  const [lightbox, setLightbox] = useState(null);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    if (data) {
      const { project, settings } = data;
      document.title = `${project.title || "Our Work"} | ${settings.name}`;
      window.scrollTo(0, 0);
    }
  }, [data]);

  if (error)
    return (
      <div className="grid min-h-screen place-items-center p-6 text-center" data-testid="project-not-found">
        <div>
          <p className="font-display text-4xl text-[#FAF7F2]">Project not found</p>
          <Link to="/#our-work" className="btn-gold mt-6 h-12" data-testid="project-not-found-back-link">Back to Our Work</Link>
        </div>
      </div>
    );
  if (!data) return <PageLoader />;
  const { project: p, settings: s } = data;
  const photos = p.media.filter((m) => m.kind === "photo");

  return (
    <div className="min-h-screen bg-[#121214]" data-testid="project-page">
      <Header settings={s} onHome={false} />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8 sm:py-14">
        <Link to="/#our-work" className="font-sub inline-flex h-11 items-center gap-2 text-sm uppercase tracking-widest text-zinc-400 transition-colors hover:text-[#FFD23F]" data-testid="project-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to Our Work
        </Link>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <StatusBadge status={p.status} testId="project-status-badge" />
          {p.is_sample && <SampleBadge testId="project-sample-badge" />}
        </div>
        {p.title && <h1 className="font-display mt-4 text-4xl leading-none text-[#FAF7F2] sm:text-5xl lg:text-6xl" data-testid="project-title">{p.title}</h1>}
        {p.description && <p className="mt-5 max-w-3xl whitespace-pre-line text-base leading-relaxed text-zinc-300 sm:text-lg" data-testid="project-description">{p.description}</p>}
        {p.media.length === 0 ? (
          <p className="mt-10 rounded-md border border-dashed border-zinc-700 p-10 text-center text-zinc-400" data-testid="project-media-empty">Photos coming soon.</p>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="project-media-grid">
            {p.media.map((m) => (
              <MediaTile key={m.id} m={m} onOpen={() => setLightbox(photos.findIndex((x) => x.id === m.id))} />
            ))}
          </div>
        )}
      </main>
      <Footer settings={s} />
      <StickyBar phone={s.primary_phone} onMessage={() => navigate("/#message")} />
      {lightbox !== null && <Lightbox photos={photos} index={lightbox} onClose={closeLightbox} onIndex={setLightbox} />}
    </div>
  );
}
